import { darken, styled } from '@mui/material/styles';
import { Box, Stack, Paper, ListItem, TextField, Typography } from '@mui/material';
import { DragDropContext } from '@hello-pangea/dnd';

export const TodoListWrapper = styled(Stack)(({ theme }) => ({
    maxWidth: `calc(100% - ${theme.spacing(6)})`,
    margin: '0 auto',
    padding: theme.spacing(3),
    height: '100%',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    position: 'relative',
    width: '100%',

    [theme.breakpoints.up('sm')]: {
        maxWidth: '600px',
    },
}));

export const DragDropWrapper = styled(DragDropContext)({
    display: 'flex',
    flexDirection: 'column',
    height: 400,
});

export const DroppablePaper = styled(Paper)(({ theme }) => ({
    backgroundColor: darken(theme.palette.background.default, 0.03),
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    overflow: 'auto',
    height: '100%',
    width: 'calc(100% - 32px)',
}));

interface DraggableBoxProps {
    isDragging: boolean;
    isDisabled: boolean;
}

export const DraggableBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isDisabled',
})<DraggableBoxProps>(({ theme, isDragging, isDisabled }) => ({
    marginBottom: theme.spacing(1),
    backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.04)' : "auto",
    borderRadius: theme.shape.borderRadius,
    boxShadow: isDragging ? theme.shadows[2] : 'none',
    pointerEvents: isDisabled ? 'none' : 'auto'
}));

export const TodoItemWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

export const DragHandle = styled(Box)(({ theme }) => ({
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.grey[500],
    '&:hover': {
        color: theme.palette.grey[700]
    },
}));

export const ActionButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

export const TodoInputWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    [theme.breakpoints.up('sm')]: {
        flexWrap: 'nowrap',
    },
}));

export const StyledListItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    boxShadow: theme.shadows[1],
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    '& *': {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    },
    '& .MuiListItemText-root': {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    }
}));

export const TextWrapper = styled(Box)({
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
});

export const StyledText = styled(Typography)(() => ({
    width: '100%',
    lineHeight: '1.5',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
}));

export const StyledTextNoSelect = styled(StyledText)(() => ({
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
}));

export const EditTextField = styled(TextField)(({ theme }) => ({
    width: '100%',
    '& .MuiInputBase-root': {
        width: '100%',
    },
    '& .MuiOutlinedInput-root': {
        padding: '8px',
    },
    '& .MuiInputBase-input': {
        height: 'auto',
        lineHeight: '1.5',
        userSelect: 'text',
        WebkitUserSelect: 'text',
        MozUserSelect: 'text',
        msUserSelect: 'text',
    }
}));

interface TodoContainerProps {
    isEditing: boolean;
}

export const TodoContainer = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isEditing',
})<TodoContainerProps>(({ theme, isEditing }) => ({
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'row',
    justifyContent: 'stretch',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    cursor: isEditing ? 'default' : 'pointer',
    minHeight: 40,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',

    width: 'calc(100% - 32px)',
    '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.04)',
        '& .action-buttons': {
            opacity: 1,
        },
    },
}));

export const MainContainer = styled(Stack)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
}));